import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck } from "lucide-react";

export default function NewsletterForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) {
      errs.name = t("newsletter.form.validation.name_required");
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email.trim())) {
      errs.email = t("newsletter.form.validation.email_required");
    }
    if (!form.consent) {
      errs.consent = t("newsletter.form.validation.consent_required");
    }
    return errs;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      const stored = JSON.parse(localStorage.getItem("costalend_leads") || "[]");
      stored.push({ name: form.name, email: form.email, ts: new Date().toISOString() });
      localStorage.setItem("costalend_leads", JSON.stringify(stored));
    } catch (err) {}
    setTimeout(() => {
      navigate("/thank_you");
    }, 150);
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="newsletter-form"
      noValidate
      className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 lg:p-10"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="newsletter-name" className="text-sm font-medium text-slate-800">
            {t("newsletter.form.name_label")}
          </Label>
          <Input
            id="newsletter-name"
            name="name"
            type="text"
            data-testid="newsletter-input-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t("newsletter.form.name_placeholder")}
            className="mt-1.5 h-11 border-slate-300 focus-visible:ring-teal-600 focus-visible:border-teal-600"
            aria-invalid={!!errors.name}
          />
          {errors.name && (
            <p className="mt-1.5 text-xs text-red-600" data-testid="newsletter-error-name">
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="newsletter-email" className="text-sm font-medium text-slate-800">
            {t("newsletter.form.email_label")}
          </Label>
          <Input
            id="newsletter-email"
            name="email"
            type="email"
            data-testid="newsletter-input-email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder={t("newsletter.form.email_placeholder")}
            className="mt-1.5 h-11 border-slate-300 focus-visible:ring-teal-600 focus-visible:border-teal-600"
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-600" data-testid="newsletter-error-email">
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 flex items-start gap-3">
        <Checkbox
          id="newsletter-consent"
          checked={form.consent}
          onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
          data-testid="newsletter-checkbox-privacy"
          className="mt-0.5 border-slate-400 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
        />
        <Label htmlFor="newsletter-consent" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
          {t("newsletter.form.consent_label")}
        </Label>
      </div>
      {errors.consent && (
        <p className="mt-2 text-xs text-red-600" data-testid="newsletter-error-consent">
          {errors.consent}
        </p>
      )}

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <Button
          type="submit"
          data-testid="newsletter-submit-button"
          disabled={submitting}
          className="h-11 rounded-md px-6 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold w-full sm:w-auto"
        >
          {submitting ? t("newsletter.form.submitting") : t("newsletter.form.submit")}
        </Button>
        <p className="text-xs text-slate-500 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-teal-600" />
          {t("newsletter.form.note")}
        </p>
      </div>
    </form>
  );
}
